import { Button, Icon } from '@global-ecom/nitro-uds/elements';
import { tw } from 'twind';
import { useRef } from 'react';

import { PurchaseOnAppCta } from 'ui/elements/PurchaseOnAppCta';
import { useTranslate } from 'hooks/useTranslations';
import { Maybe } from '__generated__/graphql';
import { useStickyButtonIntersection } from 'hooks/useStickyButton';
import { isAppExclusiveValidRange } from 'utils/isAppExclusiveValidRange';

type AddToCartButtonProps = {
  className?: string;
  disabled?: boolean;
  isAppExclusive?: Maybe<boolean>;
  loading?: boolean;
  onAddToCart: (p: any) => void;
  outOfStock?: Maybe<boolean>;
  presale?: Maybe<boolean>;
  size?: 'sm' | 'lg' | 'base';
  appOnlyDateTimeFrom?: Maybe<string>;
  appOnlyDateTimeTo?: Maybe<string>;
};

export const AddToCartButton = ({
  className,
  disabled,
  isAppExclusive,
  loading,
  onAddToCart,
  outOfStock,
  presale = false,
  size,
  appOnlyDateTimeFrom,
  appOnlyDateTimeTo,
}: AddToCartButtonProps) => {
  const t = useTranslate();
  const ref = useRef(null);

  useStickyButtonIntersection(ref, { threshold: 1 });

  if (
    isAppExclusiveValidRange(
      isAppExclusive,
      appOnlyDateTimeFrom,
      appOnlyDateTimeTo
    )
  ) {
    return <PurchaseOnAppCta className={className} size={size} />;
  }
  if (outOfStock) {
    return (
      <p
        data-test-id="out-of-stock-message"
        className={tw(
          className,
          'mt-4 flex items-center space-x-3 justify-between text-puma-red border border-puma-red font-bold px-3 py-2 text-sm rounded mb-2'
        )}
      >
        <span>{t('selectedSizeIsNotOrderable')}</span>
        <Icon name="exclamation-outline" size="2xl" />
      </p>
    );
  }

  return (
    <>
      <Button
        dataTestId="add-to-cart-button"
        loading={loading}
        variant="primary"
        className={className}
        disabled={disabled || loading}
        onClick={onAddToCart}
        label={presale ? t('preOrder') : t('addToCart')}
        size={size}
        ref={ref}
      />
    </>
  );
};
